/**
 * Helpers over the caller's access (`GET /api/v1/me`). Pages, nav, and routes come from these,
 * never from IdP group names.
 */

import type { MyAccessResponse } from "@/services/accessApi"
import { ROUTES } from "./constants"

/**
 * `internal` (sponsor) or `external` (vendor).
 */
export type Experience = "internal" | "external"

/**
 * The caller's experience: internal if any set is internal, else external if any is external, else
 * null.
 */
export function experienceOf(access: MyAccessResponse | undefined): Experience | null {
  if (!access) return null
  if (access.audiences.includes("internal")) return "internal"
  if (access.audiences.includes("external")) return "external"
  return null
}

/**
 * Whether a tenant-scoped set of the caller's carries `capability`.
 */
export function holdsTenantCapability(
  access: MyAccessResponse | undefined,
  capability: string,
): boolean {
  return access?.tenant_capabilities.includes(capability) ?? false
}

/**
 * An app area: a nav item and a landing candidate. Each route in `App.tsx` names the page that
 * gates it.
 */
export type Page =
  | "vendor"
  | "dashboard"
  | "studies"
  | "dtas"
  | "audit_reports"
  | "vendors"
  | "people"

const readsStudies = (me: MyAccessResponse, experience: Experience | null) =>
  experience === "internal" && me.set_capabilities.includes("read_study")

/**
 * A page as the nav and landing see it: its label and landing href.
 */
interface NavPage {
  page: Page
  text: string
  href: string
}

interface PageEntry extends NavPage {
  shows: (me: MyAccessResponse, experience: Experience | null) => boolean
}

/**
 * Every page in nav and landing order. A resource-scoped set counts before any grant, so a DAE
 * without grants still sees the (empty) study pages. Sponsor pages win for callers in both
 * audiences, because the experience does.
 */
const PAGES: readonly PageEntry[] = [
  {
    page: "vendor",
    text: "Dashboard",
    href: ROUTES.VENDOR_DASHBOARD,
    shows: (_, experience) => experience === "external",
  },
  { page: "dashboard", text: "Dashboard", href: ROUTES.DAE_DASHBOARD, shows: readsStudies },
  { page: "studies", text: "Studies", href: "/studies", shows: readsStudies },
  { page: "dtas", text: "DTAs", href: "/dtas", shows: readsStudies },
  { page: "audit_reports", text: "Audit Reports", href: "/dae/audit-reports", shows: readsStudies },
  {
    page: "people",
    text: "People",
    href: ROUTES.PRINCIPALS,
    shows: (me) => me.tenant_capabilities.includes("read_principal_directory"),
  },
  {
    page: "vendors",
    text: "Vendors",
    href: ROUTES.VENDOR_MANAGEMENT,
    shows: (me) => me.tenant_capabilities.includes("read_vendor"),
  },
]

/**
 * The caller's pages in nav order. They land on the first; none means access is not configured.
 */
export function pagesFor(access: MyAccessResponse | undefined): NavPage[] {
  if (!access) return []
  const experience = experienceOf(access)
  return PAGES.filter((p) => p.shows(access, experience)).map(({ page, text, href }) => ({
    page,
    text,
    href,
  }))
}
